import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import { useTranslatedAssets } from '../../hooks/useTranslatedAssets';

/**
 * Higher-order component that provides translation functionality for page assets.
 * This HOC integrates with the existing hostedAssets system and adds automatic
 * translation with caching when the locale changes.
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableTranslation - Whether to enable translation (default: true)
 * @param {string[]} options.assetNames - Specific asset names to translate (optional)
 * @param {number} options.cacheTimeoutMs - Cache timeout in milliseconds (optional)
 * @returns {Function} - HOC function that takes a component and returns enhanced component
 */
export const withTranslatedAssets = (options = {}) => (WrappedComponent) => {
  const {
    enableTranslation = true,
    assetNames = null,
    cacheTimeoutMs = null
  } = options;

  const WithTranslatedAssetsComponent = (props) => {
    const config = useConfiguration();
    const currentLocale = useSelector(state => state.ui?.language) || 'en';
    const defaultLocale = config.localization?.defaultLocale || 'en';
    // The language the CMS content is actually written in (may differ from the UI defaultLocale)
    const sourceLanguage = process.env.REACT_APP_TRANSLATION_SOURCE_LANGUAGE || 'en';
    
    // Get original assets from props (passed by mapStateToProps)
    const { pageAssetsData, inProgress, error } = props;

    // Determine which assets to translate
    // Skip only if the current locale matches the CMS source language (no translation needed)
    const assetsToTranslate = useMemo(() => {
      if (!pageAssetsData || !enableTranslation || currentLocale === sourceLanguage) {
        return {};
      }

      // If specific asset names are provided, filter to those
      if (assetNames && assetNames.length > 0) {
        return Object.keys(pageAssetsData).reduce((filtered, key) => {
          if (assetNames.includes(key)) {
            filtered[key] = pageAssetsData[key];
          }
          return filtered;
        }, {});
      }

      // Otherwise, include all PageBuilder-compatible assets
      return Object.keys(pageAssetsData).reduce((filtered, key) => {
        const assetData = pageAssetsData[key];
        // Check if this looks like a PageBuilder asset (has sections or blocks)
        if (assetData?.data && (assetData.data.sections || assetData.data.blocks)) {
          filtered[key] = assetData;
        }
        return filtered;
      }, {});
    }, [pageAssetsData, enableTranslation, currentLocale, sourceLanguage, assetNames]);

    // Use our translation hook
    const {
      translatedAssets,
      loading: translationLoading,
      error: translationError,
      cacheStats
    } = useTranslatedAssets(assetsToTranslate, currentLocale, {
      enabled: enableTranslation && currentLocale !== sourceLanguage,
      cacheTimeoutMs
    });

    // Merge translated assets with original assets
    const enhancedPageAssetsData = useMemo(() => {
      if (!pageAssetsData) return null;
      
      // If we're not translating or current locale is the source language, return original data
      if (!enableTranslation || currentLocale === sourceLanguage || !translatedAssets) {
        return pageAssetsData;
      }

      // Merge translated assets back into the full asset data
      return {
        ...pageAssetsData,
        ...translatedAssets
      };
    }, [pageAssetsData, translatedAssets, enableTranslation, currentLocale, sourceLanguage]);

    // Combine loading states
    const isLoading = inProgress || (translationLoading && Object.keys(assetsToTranslate).length > 0);
    
    // Combine errors - prefer original error over translation error
    const combinedError = error || translationError;

    // Add translation metadata to props
    const enhancedProps = {
      ...props,
      pageAssetsData: enhancedPageAssetsData,
      inProgress: isLoading,
      error: combinedError,
      // Additional translation metadata
      translationMeta: {
        isTranslated: currentLocale !== sourceLanguage && !!translatedAssets,
        currentLocale,
        defaultLocale,
        assetsTranslated: translatedAssets ? Object.keys(translatedAssets) : [],
        cacheStats: enableTranslation ? cacheStats : null,
        translationError
      }
    };

    return React.createElement(WrappedComponent, enhancedProps);
  };

  // Set display name for debugging
  WithTranslatedAssetsComponent.displayName = `withTranslatedAssets(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithTranslatedAssetsComponent;
};

/**
 * Hook version for functional components that need translation functionality
 * without using the HOC pattern.
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableTranslation - Whether to enable translation
 * @param {string[]} options.assetNames - Specific asset names to translate
 * @param {number} options.cacheTimeoutMs - Cache timeout in milliseconds
 * @returns {Object} Enhanced asset data with translation support
 */
export const useTranslatedPageAssets = (options = {}) => {
  const config = useConfiguration();
  const currentLocale = config.localization?.locale || 'en';
  const defaultLocale = config.localization?.defaultLocale || 'en';
  
  const { pageAssetsData, inProgress, error } = useSelector(state => ({
    pageAssetsData: state.hostedAssets?.pageAssetsData,
    inProgress: state.hostedAssets?.inProgress,
    error: state.hostedAssets?.error,
  }));

  // Use the HOC logic
  const TemporaryComponent = () => null;
  const EnhancedComponent = withTranslatedAssets(TemporaryComponent, options);
  
  // This is a bit of a hack, but it reuses the HOC logic
  // In a real implementation, you'd extract the common logic to a shared function
  const enhancedProps = EnhancedComponent({ pageAssetsData, inProgress, error });
  
  return {
    pageAssetsData: enhancedProps.pageAssetsData,
    inProgress: enhancedProps.inProgress,
    error: enhancedProps.error,
    translationMeta: enhancedProps.translationMeta
  };
};

export default withTranslatedAssets;